import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { useSelector } from 'react-redux';
import { CellProps } from 'react-table';
import moment from 'moment-timezone';
import { Participant } from '@app/api';
import classnames from 'classnames';

import {
  useBoolModalState,
  useModalState,
  WithRightClosablePane,
} from '@app/core';

import { getPanel, selectCurrentPanel, selectFastParticipants } from 'store';
import { useFetchedEntity } from 'hooks/useFetched';

import {
  ColumnsFilters,
  DataTable,
  DotsMenu,
  MainDashboardPage,
  MenuItem,
  useFeatureRequiredCallback,
  PageTitle,
  useDatatable,
} from 'components';

import { ParticipantPane } from './components/ParticipantPane';
import { DeleteParticipantsModal } from './components/DeleteParticipantsModal';
import { CustomColumnModal } from './components/CustomColumnModal';
import { InviteParticipantsModal } from './components/InviteParticipantsModal';
import { TagsListCell } from './components/TagsList';
import { clientSpecialConfigs } from 'config/clientSpecialConfigs';
import { ParticipantListLayout } from './ParticipantListLayout';
import { AnalyticsCategoryProvider, Categories } from '@app/analytics';
import { RedExcludedCell } from './components/RedExcludedCell';
import { Feature, ParticipantFieldType, useParticipantFields } from '@app/api';
import { useGetParticipantDisplayValue } from '@app/participant-fields';
import { useCurrentOrganizationId } from 'queries';
import { SelectionParticipantFooter } from 'pages/Dashboard/Panel/components/SelectionParticipantFooter';
import { NewFeatureBanner } from 'components/NewFeatureBanner';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  settings: {
    color: theme.colors.inkTertiary,
    fontSize: theme.iconSize.large,
    display: 'flex',
    alignItems: 'center',
  },
  searchFilterGroup: {
    display: 'flex',
  },
  search: {
    marginRight: theme.marginBase,
  },
  table: {
    marginBottom: theme.marginBase * 10,
  },
  dotMenu: {
    [theme.media.mobile]: {
      position: 'absolute',
      top: theme.marginBase * 2,
      right: theme.marginBase,
    },
  },
  buttonsDisplay: {
    display: 'flex',
    justifyContent: 'center',
    marginRight: -theme.marginBase * 2,
    [theme.media.mobile]: {
      flexDirection: 'column',
      marginRight: 0,
    },
  },
  button: {
    marginRight: theme.marginBase * 2,
    [theme.media.mobile]: {
      marginBottom: theme.marginBase * 2,
      marginRight: 0,
    },
  },
  addIcon: {
    color: theme.colors.blue,
  },
}));

const emptyArray: string[] = [];

export const ParticipantList = () => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const [paneOpened, openPane, closePane] = useBoolModalState();
  const [selected, setSelected] = useState<string | undefined>();

  const openCreation = useCallback(() => {
    openPane();
    setSelected(undefined);
  }, [openPane, setSelected]);

  const openEdition = useCallback(
    (id: string, e?: SyntheticEvent) => {
      if (id === selected) {
        return;
      }
      openPane();
      setSelected(id);
      if (e) {
        e.stopPropagation();
      }
    },
    [openPane, setSelected, selected],
  );

  const resetPane = useCallback(() => {
    if (!paneOpened) {
      return;
    }
    closePane();
    setSelected(undefined);
  }, [closePane, setSelected, paneOpened]);

  const [
    toDeleteParticipantsList,
    setToDeleteParticipantsList,
    clearDeleteParticipantList,
  ] = useModalState<string[]>(emptyArray);
  const deleteParticipant = useCallback(
    (id: string) => setToDeleteParticipantsList([id]),
    [setToDeleteParticipantsList],
  );

  const [
    toInviteParticipantsList,
    setToInviteParticipantsList,
    clearInviteParticipantList,
  ] = useModalState<string[]>(emptyArray);

  const [
    customColumnNotificationOpen,
    openCustomColumnNotificationCallback,
    closeCustomColumnNotification,
  ] = useBoolModalState();

  const openCustomColumnNotification = useFeatureRequiredCallback(
    Feature.CUSTOM_PARTICIPANT_FIELD,
    openCustomColumnNotificationCallback,
  );

  const panel = useSelector(selectCurrentPanel);
  const panelId = panel && panel.id;
  const participants = useSelector((state: any) =>
    selectFastParticipants(state, panelId),
  );

  useFetchedEntity(getPanel, panelId);
  const { data: participantFields } = useParticipantFields();
  const getParticipantDisplayValue = useGetParticipantDisplayValue();

  const currentOrganizationId = useCurrentOrganizationId();
  const specialFields = useMemo(() => {
    const fields = clientSpecialConfigs.find(
      ({ orgId }) => orgId === currentOrganizationId,
    );
    if (!fields) {
      return [];
    }
    return fields.specialFields;
  }, [currentOrganizationId]);

  const participantFieldsNameWithArrayFilter = useMemo(() => {
    if (!participantFields) {
      return [];
    }
    return participantFields
      .filter((field) => field?.table?.useArrayFilter)
      .map((field) => field.name);
  }, [participantFields]);

  const columns = useMemo(() => {
    if (!participantFields) {
      return [];
    }
    const participantFieldsColumns = participantFields.map((field) => {
      let column: any = {
        accessor: (p: Participant) => getParticipantDisplayValue(p, field),
        Header: field.label,
        singleLine: !!field.table && !!field.table.singleLine,
        filter:
          field.table && field.table.filter ? field.table.filter : undefined,
      };

      if (field.type === ParticipantFieldType.TAGS) {
        column = {
          ...column,
          Cell: (props: CellProps<Participant>) => (
            // @ts-ignore
            <TagsListCell {...props} key={paneOpened ? '1' : '0'} />
          ),
          Filter: ColumnsFilters.TagsFilter,
          filter: 'tags',
        };
      } else if (participantFieldsNameWithArrayFilter.includes(field.name)) {
        column = {
          ...column,
          ...column,
          Filter: (props: any) => {
            return ColumnsFilters.ParticipantFieldChoiceFilter({
              column: props.column,
              field,
            });
          },
          filter: (rows: any, id: any, filterValue: any) => {
            if (field.type === ParticipantFieldType.MULTIPLE_CHOICE) {
              return rows.filter((row: any) => {
                const rowValue = row.values[id];
                const rowValueArray = rowValue.split(', ');
                return filterValue.every((value: any) =>
                  rowValueArray.includes(value),
                );
              });
            }
            return rows.filter((row: any) => {
              const rowValue = row.values[id];
              if (!filterValue || filterValue.length === 0) {
                return true;
              }
              if (rowValue) {
                return filterValue.includes(rowValue);
              }
              return false;
            });
          },
        };
      } else if (
        field.type === ParticipantFieldType.DATE &&
        field.displayAsAge
      ) {
        column = {
          ...column,
          accessor: (p: Participant) =>
            p[field.name]
              ? moment().diff(String(p[field.name]), 'years')
              : undefined,
          Filter: ColumnsFilters.MinMaxFilter,
          filter: 'between',
          sortType: 'number',
        };
      } else if (field.table && field.table.Cell === 'RED_EXCLUDED') {
        column.Cell = RedExcludedCell;
      }
      return column;
    });

    return [
      ...participantFieldsColumns,
      ...specialFields.map((field) => ({
        accessor: field.accessor || field.name,
        Header: field.clientLabel,
      })),
      {
        accessor: 'id',
        notOrderable: true,
        disableFilters: true,
        centered: true,
        disableGlobalFilter: true,
        Header: () => (
          <button
            className={classes.settings}
            onClick={openCustomColumnNotification}
          >
            <i className={classnames('ri-add-line', classes.addIcon)} />
          </button>
        ),
        width: 72,
        Cell: (props: CellProps<Participant>) => (
          <DotsMenu className={classes.dotMenu}>
            <MenuItem
              label={t('participants.list.table.actions.edit')}
              onClick={() => openEdition(props.row.id)}
            />
            <MenuItem
              label={t('participants.list.table.actions.delete')}
              onClick={() => setToDeleteParticipantsList([props.row.id])}
            />
          </DotsMenu>
        ),
      },
    ];
  }, [
    getParticipantDisplayValue,
    openEdition,
    setToDeleteParticipantsList,
    classes,
    paneOpened,
    openCustomColumnNotification,
    participantFields,
    specialFields,
    t,
  ]);

  const tableInfos = useDatatable({
    data: participants,
    columns,
  });

  const onDelete = useCallback(
    () => setToDeleteParticipantsList(tableInfos.selectedRowIds),
    [setToDeleteParticipantsList, tableInfos],
  );
  const onInvite = useCallback(() => {
    setToInviteParticipantsList(tableInfos.selectedRowIds);
  }, [setToInviteParticipantsList, tableInfos]);
  const Pane = useCallback(
    (params: any) => (
      <ParticipantPane
        {...params}
        panelId={panelId}
        participantId={selected}
        deleteParticipant={deleteParticipant}
      />
    ),
    [panelId, selected, deleteParticipant],
  );

  const deselectRows = useCallback(
    () => tableInfos.toggleAllRowsSelected(false),
    [tableInfos],
  );

  return (
    <AnalyticsCategoryProvider category={Categories.PANEL}>
      <PageTitle title={t('participants.metaTitle')} />
      <MainDashboardPage>
        <NewFeatureBanner />
        <WithRightClosablePane
          pane={Pane}
          isOpen={paneOpened}
          onRequestClose={resetPane}
          noScroll
        >
          <ParticipantListLayout
            resetPane={resetPane}
            tableInfos={tableInfos}
            openCreation={openCreation}
            participantsCount={participants.length}
          >
            <DataTable
              onRowClick={openEdition}
              tableInfos={tableInfos}
              count={participants.length}
              selectedId={selected}
              className={classes.table}
            />
          </ParticipantListLayout>
          <SelectionParticipantFooter
            selectedRowIds={tableInfos.selectedRowIds}
            onDelete={onDelete}
            onInvite={onInvite}
          />
          {panelId && (
            <DeleteParticipantsModal
              isOpen={!!toDeleteParticipantsList.length}
              onRequestClose={clearDeleteParticipantList}
              toDeleteParticipantsList={toDeleteParticipantsList}
              onDeleteSuccess={resetPane}
              panelId={panelId}
            />
          )}
          <InviteParticipantsModal
            isOpen={!!toInviteParticipantsList.length}
            onRequestClose={clearInviteParticipantList}
            participants={toInviteParticipantsList}
            onInviteSuccess={deselectRows}
          />
          <CustomColumnModal
            isOpen={customColumnNotificationOpen}
            onRequestClose={closeCustomColumnNotification}
            panelId={panelId}
          />
        </WithRightClosablePane>
      </MainDashboardPage>
    </AnalyticsCategoryProvider>
  );
};
